import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useMemo,
} from "react";
import {
  TextField,
  IconButton,
  Button,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import CloseIcon from "@mui/icons-material/Close";
import useFollowUp from "../../hooks/useFollowUp";
import styles from "./chatwindow.module.css";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../store";
import CircularProgress from "@mui/material/CircularProgress";
import LoadingSpinner from "../LoadingSpinner";
import {
  setTranslationFilters,
  setProcurementFilters,
} from "../../store/filtersSlice";
import useEnhancePrompt from "../../hooks/useEnhancePrompt";
import { v4 as uuidv4 } from "uuid";
import { Agents } from "../../types/master";
import { v4 as uuid } from "uuid";
import { sendMessageToAgent } from "../../store/userSlice";
import attachIcon from "../../assets/images/addIcon.svg";
import micIcon from "../../assets/images/mic.svg";
import sendIcon from "../../assets/images/voice.svg";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import {
  MAX_FILE_SIZE,
  AGENTS_WITH_PERSISTENT_HR_FILTERS,
  AGENTS_WITH_FILE_ATTACHMENT,
  PROCUREMENT_TYPE_OPTIONS,
  AGENTS_WITH_END_FLOW,
} from "../../utils/constants";
import { getPlaceholderByAgentId } from "../../utils/helper";
import RefinementFilters from "./RefinementFilters";
import {
  resetTemplateReviewerFeedback,
  resetTemplateReviewerChecklistFeedback,
  resetConsolidatorFeedback,
  resetFinanceFeedback,
  resetLegalFeedback,
  resetProcurementFeedback,
  resetBusinessFeedback,
  resetSowAutoFillerFeedback,
  resetHumanCapitalFeedback,
} from "../../store/filtersSlice";

export type MessageInputHandle = {
  // Programmatically set and send a prompt
  sendPrompt: (text: string) => Promise<void>;
};

const NDA_AGENT_ID = 48;
const NDA_ENDPOINT = "/stream/nda";
const NDA_KEYWORD_REGEX =
  /\b(nda|non[-\s]?disclosure|confidentiality agreement|mutual nda|unilateral nda)\b/i;
const MARKETING_AGENT_IDS = new Set([1, 11]);

type MessageInputProps = {
  agentId?: string;
  currentAgent: Agents | undefined;
  value: string;
  setValue: any;
  showDisclaimer?: boolean;
  showGenerateImageToggle?: boolean;
  disableImageToggle?: boolean;
  showUploadProfilesToggle?: boolean;
  disableUploadProfilesToggle?: boolean;
  disabled?: boolean;
};

const MessageInput = forwardRef<MessageInputHandle, MessageInputProps>(
  (
    {
      agentId,
      currentAgent,
      value,
      setValue,
      showDisclaimer = true,
      showGenerateImageToggle = false,
      disableImageToggle = false,
      showUploadProfilesToggle = false,
      disableUploadProfilesToggle = false,
      disabled = false,
    }: MessageInputProps,
    ref,
  ) => {
    const dispatch = useAppDispatch();
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("sessionId");

    const { selectedAgent, currentChat, loading } = useSelector(
      (state: RootState) => state.user,
    );

    const {
      translationFilters,
      procurementFilters,
      templateReviewerFeedback,
      templateReviewerChecklistFeedback,
      consolidatorFeedback,
      financeFeedback,
      legalFeedback,
      procurementFeedback,
      businessFeedback,
      sowAutoFillerFeedback,
      humanCapitalFeedback,
    } = useSelector((state: RootState) => state.filters);

    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [showAllFilesDialog, setShowAllFilesDialog] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Dual file upload state for agent 52
    const [ourDocument, setOurDocument] = useState<File | null>(null);
    const [counterpartyDocument, setCounterpartyDocument] = useState<File | null>(null);
    const ourDocInputRef = useRef<HTMLInputElement>(null);
    const counterpartyDocInputRef = useRef<HTMLInputElement>(null);
    const [resumeNegotiation, setResumeNegotiation] = useState<boolean>(false);
    const [threadId, setThreadId] = useState<string>("");

    // File upload state for agent 55 (Human Capital)
    const [jdDocument, setJdDocument] = useState<File | null>(null);
    const jdDocInputRef = useRef<HTMLInputElement>(null);

    const [isOn, setIsOn] = useState(false);
    const [isUploadProfilesOn, setIsUploadProfilesOn] = useState(false);

    // const { enhancing, enhance } = useEnhancePrompt();
    // const { generateFollowUp } = useFollowUp();
    // const dispatch = useDispatch<AppDispatch>();
    // const { messages, isChatOpen, loading } = useSelector(
    //   (state: RootState) => state.chat
    // );
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
    const baseValueRef = useRef<string>("");

    const {
      transcript,
      listening,
      resetTranscript,
      browserSupportsSpeechRecognition,
    } = useSpeechRecognition();

    // Expose imperative API to parent via forwarded ref
    useImperativeHandle(ref, () => ({
      sendPrompt: async (text: string) => {
        setValue(text);
        setTimeout(
          () => inputRef.current && (inputRef.current as HTMLElement).focus(),
          0,
        );
      },
    }));

    // Auto-focus input when chat is opened or when there are no messages
    // useEffect(() => {
    //   if (isChatOpen || messages.length === 0) {
    //     // Small delay to ensure the input is rendered
    //     setTimeout(() => {
    //       inputRef.current?.focus();
    //     }, 100);
    //   }
    // }, [isChatOpen, messages.length]);

    // const submit = useCallback(async () => {
    //   const trimmed = value.trim();
    //   if (!trimmed) return;

    //   // Clear input immediately
    //   setValue("");

    //   const userMessage = {
    //     id: uuidv4(),
    //     sender: "user" as const,
    //     text: trimmed,
    //   };
    //   dispatch(addUserMessage(userMessage));
    //   await dispatch(sendQuery({ userMessage: trimmed, agentId }));
    // }, [value, dispatch, agentId]);

    // Expose imperative API
    // useImperativeHandle(
    //   ref,
    //   () => ({
    //     sendPrompt: async (text: string) => {
    //       // Only set the input text; do NOT auto-send
    //       setValue(text);
    //       // Focus the input so user can hit Enter or click Send
    //       setTimeout(() => inputRef.current?.focus(), 0);
    //     },
    //   }),
    //   []
    // );

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files) return;

      // For agent 103, prevent file selection if documents have already been uploaded
      if (currentAgent?.id === 103 && hasAgent103UploadedDocuments) {
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      // MOU agent (ID 54) only accepts PDF and DOCX files
      const mouValidTypes = [
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];

      const validTypes = [
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/msword",
        "text/plain",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];

      const fileArray = Array.from(files);

      const filteredFiles = fileArray.filter((file) => {
        // Use MOU-specific validation for agent 54
        const typesToCheck = currentAgent?.id === 54 ? mouValidTypes : validTypes;
        const isValidType = typesToCheck.includes(file.type);
        const isValidSize = file.size <= MAX_FILE_SIZE;

        if (!isValidType) {
          if (currentAgent?.id === 54) {
            alert(
              `${file.name} is not allowed. Only PDF and DOCX files are permitted for MOU.`,
            );
          } else {
            alert(
              `${file.name} is not allowed. Only PDF, DOCX, DOC, TXT, XLS, and XLSX files are permitted.`,
            );
          }
          return false;
        }

        if (!isValidSize) {
          alert(`${file.name} is larger than 10 MB and cannot be uploaded.`);
          return false;
        }

        return true;
      });

      if (filteredFiles.length > 0) {
        setSelectedFiles((prev) => {
          // MOU agent (ID 54) accepts only 1 file at a time
          if (currentAgent?.id === 54) {
            if (prev.length > 0) {
              alert("You can only upload one file at a time for MOU. Please remove the existing file first.");
              return prev;
            }
            // Only take the first file
            return [filteredFiles[0]];
          }
          // Agent ID 30 has a limit of 5 files
          if (currentAgent?.id === 30) {
            const totalFiles = prev.length + filteredFiles.length;
            if (totalFiles > 5) {
              const allowedCount = Math.max(0, 5 - prev.length);
              if (allowedCount === 0) {
                alert("You can only upload up to 5 files for this agent.");
                return prev;
              } else {
                alert(
                  `You can only upload ${allowedCount} more file(s). Only the first ${allowedCount} file(s) will be added.`,
                );
                return [...prev, ...filteredFiles.slice(0, allowedCount)];
              }
            }
          }
          return [...prev, ...filteredFiles];
        });
      }

      // Reset input to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };

    const handleRemoveFile = (index: number) => {
      setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    // Handler for agent 52 dual file upload
    const handleDualFileSelect = (
      event: React.ChangeEvent<HTMLInputElement>,
      type: "our" | "counterparty",
    ) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const file = files[0]; // Only take the first file

      const validTypes = [
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/msword",
      ];

      const isValidType = validTypes.includes(file.type);
      const isValidSize = file.size <= MAX_FILE_SIZE;

      if (!isValidType) {
        alert(
          `${file.name} is not allowed. Only PDF, DOCX, and DOC files are permitted.`,
        );
        return;
      }

      if (!isValidSize) {
        alert(`${file.name} is larger than 10 MB and cannot be uploaded.`);
        return;
      }

      if (type === "our") {
        setOurDocument(file);
      } else {
        setCounterpartyDocument(file);
      }

      // Reset input to allow selecting the same file again
      if (type === "our" && ourDocInputRef.current) {
        ourDocInputRef.current.value = "";
      } else if (type === "counterparty" && counterpartyDocInputRef.current) {
        counterpartyDocInputRef.current.value = "";
      }
    };

    const handleRemoveDualFile = (type: "our" | "counterparty") => {
      if (type === "our") {
        setOurDocument(null);
      } else {
        setCounterpartyDocument(null);
      }
    };

    // Handler for agent 55 JD file upload
    const handleJdFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      const file = files[0]; // Only take the first file

      const validTypes = [
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/msword",
      ];

      const isValidType = validTypes.includes(file.type);
      const isValidSize = file.size <= MAX_FILE_SIZE;

      if (!isValidType) {
        alert(
          `${file.name} is not allowed. Only PDF, DOCX, and DOC files are permitted.`,
        );
        return;
      }

      if (!isValidSize) {
        alert(`${file.name} is larger than 10 MB and cannot be uploaded.`);
        return;
      }

      setJdDocument(file);

      // Reset input to allow selecting the same file again
      if (jdDocInputRef.current) {
        jdDocInputRef.current.value = "";
      }
    };

    const handleRemoveJdFile = () => {
      setJdDocument(null);
    };

    const shouldRoutePromptToNda = useCallback(() => {
      if (!currentAgent || !MARKETING_AGENT_IDS.has(currentAgent.id)) {
        return false;
      }

      if (NDA_KEYWORD_REGEX.test(value)) {
        return true;
      }

      return selectedFiles.some((file) => NDA_KEYWORD_REGEX.test(file.name));
    }, [currentAgent, selectedFiles, value]);

    const handleSubmit = async () => {
      // If currently listening, stop recognition and append final transcript
      if (listening) {
        try {
          SpeechRecognition.stopListening();
        } catch (e) {
          console.warn("Error stopping speech recognition", e);
        }
        // Ensure final transcript is included
        const finalText = (baseValueRef.current || "") + (transcript || "");
        setValue(finalText);
        baseValueRef.current = "";
        resetTranscript();
      }

      // Validation for agent 52: requires both files OR a text message
      if (currentAgent?.id === 52) {
        const hasBothFiles = ourDocument && counterpartyDocument;
        const hasMessage = value.trim();
        if (!hasBothFiles && !hasMessage) {
          return;
        }
      } else if (currentAgent?.id === 55) {
        // Validation for agent 55: requires JD file OR a text message
        const hasJdFile = jdDocument !== null;
        const hasMessage = value.trim();
        if (!hasJdFile && !hasMessage) {
          return;
        }
      } else {
        // Standard validation for other agents
        if (!value.trim() && selectedFiles.length === 0) return;

        // Agent ID 30: First message requires both text and files, subsequent messages require only text
        if (currentAgent?.id === 30) {
          if (
            isAgent30FirstMessage &&
            (!value.trim() || selectedFiles.length === 0)
          ) {
            return;
          }
          if (!isAgent30FirstMessage && !value.trim()) {
            return;
          }
        }
      }

      const questionId = uuid();
      const timestamp = new Date().toISOString();

      try {
        const NDA_HITL_AGENT_IDS = [
          "nda_orchestrator",
          "nda_intake_agent",
          "nda_counterparty_agent",
          "nda_conflict_agent",
          "nda_template_selector_agent",
          "nda_draft_field_capture_agent",
          "nda_drafting_agent",
          "nda_validation_agent",
          "nda_reviewer_agent",
          "nda_parser_agent",
          "nda_qa_agent",
        ];
        const lastAgentMsg = [...currentChat]
          .reverse()
          .find((m) => m.sender === "agent");
        const isNdaHitl = NDA_HITL_AGENT_IDS.includes(
          lastAgentMsg?.agentIdName || "",
        );
        const shouldForceNdaRouting = shouldRoutePromptToNda();
        const isNdaWorkflowRouting = isNdaHitl || shouldForceNdaRouting;
        const effectiveEndpoint = isNdaWorkflowRouting
          ? NDA_ENDPOINT
          : currentAgent?.endpoint;
        const effectiveAgentId = isNdaWorkflowRouting
          ? NDA_AGENT_ID
          : currentAgent!.id;
        const initialStreamExecutorId = isNdaWorkflowRouting
          ? isNdaHitl
            ? lastAgentMsg?.agentIdName || "nda_orchestrator"
            : "nda_orchestrator"
          : undefined;

        const payload: any = {
          agentId: effectiveAgentId,
          message:
            isNdaWorkflowRouting && value.trim()
              ? (() => {
                  const trimmed = value.trim();
                  if (trimmed.length < 24 || trimmed.length % 2 !== 0) {
                    return trimmed;
                  }
                  const half = trimmed.length / 2;
                  return trimmed.slice(0, half) === trimmed.slice(half)
                    ? trimmed.slice(0, half)
                    : trimmed;
                })()
              : value,
          questionId,
          timestamp,
          agentEndpoint: effectiveEndpoint,
          initialStreamExecutorId,
          sessionId,
        };

        // Handle files differently for agent 52 and 55
        if (currentAgent?.id === 52) {
          payload.our_file = ourDocument || undefined;
          payload.counterparty_file = counterpartyDocument || undefined;
          if (resumeNegotiation && threadId.trim()) {
            payload.thread_id = threadId.trim();
          }
        } else if (currentAgent?.id === 55) {
          // Agent 55: Include JD file in files array if present
          payload.files = jdDocument ? [jdDocument] : [];
        } else {
          payload.files = selectedFiles;
        }

        // Generate-social-media-images UI removed for Press Release (11) and Social Media (106); always off
        if (
          !isNdaWorkflowRouting &&
          (currentAgent?.id === 11 || currentAgent?.id === 106)
        ) {
          payload.isImageGeneration = false;
        }

        // Include translation settings for agent ID 7
        if (currentAgent?.id === 7) {
          payload.translation = translationFilters.translation;
          payload.translationType = translationFilters.translationType;
        }

        // Include procurement type for agent ID 20, 21, and 46
        if (currentAgent?.id === 20) {
          payload.procurement_type = procurementFilters.procurementType;
        }
        if (currentAgent?.id === 21) {
          payload.procurement_type = "sow_filler";
        }
        if (currentAgent?.id === 46) {
          payload.procurement_type = "template";
        }

        // Include template reviewer feedback arrays if any exist (for TemplateReviewerAgent)
        if (
          templateReviewerFeedback.accept_array.length > 0 ||
          templateReviewerFeedback.reject_array.length > 0 ||
          templateReviewerFeedback.modify_array.length > 0
        ) {
          // Map frontend field names back to backend field names
          const mapBackendFields = (item: any) => {
            const mapped = { ...item };

            // KeyFinding: issue_clause → issue_or_clause (backend accepts both)
            if (mapped.issue_clause !== undefined) {
              mapped.issue_or_clause = mapped.issue_clause;
              delete mapped.issue_clause;
            }

            // RFPDeviation: section → issue (backend expects 'issue' as identifier)
            if (
              mapped.section !== undefined &&
              mapped.observed_deviation !== undefined
            ) {
              mapped.issue = mapped.section;
              delete mapped.section;
            }

            // KeyGapRisk: title → issue_title (only for gaps which have section + title)
            if (mapped.title !== undefined && mapped.section !== undefined) {
              mapped.issue_title = mapped.title;
              delete mapped.title;
            }

            // NegotiationPosition: topic_issue → issue (backend expects 'issue')
            if (
              mapped.topic_issue !== undefined &&
              mapped.primary_owner !== undefined
            ) {
              mapped.issue = mapped.topic_issue;
              delete mapped.topic_issue;
            }

            // PlaybookEntry (Consolidator): issue_topic → issue (backend expects 'issue')
            if (
              mapped.issue_topic !== undefined &&
              mapped.primary_position !== undefined
            ) {
              mapped.issue = mapped.issue_topic;
              delete mapped.issue_topic;
            }

            return mapped;
          };

          // Clean up _key property before sending, then map field names
          const cleanArray = (arr: any[]) =>
            arr.map(({ _key, ...rest }) => mapBackendFields(rest));

          payload.accept_array = cleanArray(
            templateReviewerFeedback.accept_array,
          );
          payload.reject_array = cleanArray(
            templateReviewerFeedback.reject_array,
          );
          payload.modify_array = cleanArray(
            templateReviewerFeedback.modify_array,
          );

          // Reset after sending
          dispatch(resetTemplateReviewerFeedback());
        }

        // Include template reviewer checklist feedback arrays if any exist (for TemplateReviewerChecklistAgent)
        // Only get feedback from the most recent checklist message to avoid sending old selections
        const mostRecentChecklistMessage = currentChat
          .slice()
          .reverse()
          .find(
            (msg) =>
              msg.sender === "agent" &&
              msg.agentIdName === "template_reviewer_checklist_agent",
          );

        // Clean up old checklist feedback entries (keep only the most recent)
        // This ensures we don't accumulate stale feedback in Redux
        if (mostRecentChecklistMessage?.id) {
          Object.keys(templateReviewerChecklistFeedback).forEach((id) => {
            if (id !== mostRecentChecklistMessage.id) {
              dispatch(resetTemplateReviewerChecklistFeedback(id));
            }
          });

          // Only include feedback if it exists and has content
          const checklistFeedback =
            templateReviewerChecklistFeedback[mostRecentChecklistMessage.id];
          if (
            checklistFeedback &&
            (checklistFeedback.reject_array.length > 0 ||
              checklistFeedback.modify_array.length > 0 ||
              checklistFeedback.add_new_array.length > 0)
          ) {
            // Clean up _key property before sending
            const cleanArray = (arr: any[]) =>
              arr.map(({ _key, ...rest }) => rest);

            payload.reject_array = cleanArray(checklistFeedback.reject_array);
            payload.modify_array = cleanArray(checklistFeedback.modify_array);
            payload.add_new_array = cleanArray(checklistFeedback.add_new_array);
          }
        }

        // Check if the user message is a simple approval keyword
        // If so, don't send the consolidator/reviewer arrays (they were already sent with explicit changes)
        const userMessage = value.trim().toLowerCase();
        const isSimpleApproval =
          userMessage === "yes" ||
          userMessage === "approve" ||
          userMessage === "proceed" ||
          userMessage === "yes proceed" ||
          userMessage === "ok" ||
          userMessage === "okay" ||
          userMessage === "confirm" ||
          userMessage === "continue" ||
          userMessage === "go ahead";

        // Include consolidator feedback arrays if any exist AND this is not a simple approval
        const hasConsolidatorFeedback =
          consolidatorFeedback.consolidated_issues.length > 0 ||
          consolidatorFeedback.accept_array.length > 0 ||
          consolidatorFeedback.reject_array.length > 0 ||
          consolidatorFeedback.modify_array.length > 0;

        if (hasConsolidatorFeedback && !isSimpleApproval) {
          // Map frontend field names back to backend field names
          const mapBackendFields = (item: any) => {
            const mapped = { ...item };

            // KeyFinding: issue_clause → issue_or_clause (backend accepts both)
            if (mapped.issue_clause !== undefined) {
              mapped.issue_or_clause = mapped.issue_clause;
              delete mapped.issue_clause;
            }

            // RFPDeviation: section → issue (backend expects 'issue' as identifier)
            if (
              mapped.section !== undefined &&
              mapped.observed_deviation !== undefined
            ) {
              mapped.issue = mapped.section;
              delete mapped.section;
            }

            // KeyGapRisk: title → issue_title (only for gaps which have section + title)
            if (mapped.title !== undefined && mapped.section !== undefined) {
              mapped.issue_title = mapped.title;
              delete mapped.title;
            }

            // NegotiationPosition: topic_issue → issue (backend expects 'issue')
            if (
              mapped.topic_issue !== undefined &&
              mapped.primary_owner !== undefined
            ) {
              mapped.issue = mapped.topic_issue;
              delete mapped.topic_issue;
            }

            // PlaybookEntry (Consolidator): issue_topic → issue (backend expects 'issue')
            if (
              mapped.issue_topic !== undefined &&
              mapped.primary_position !== undefined
            ) {
              mapped.issue = mapped.issue_topic;
              delete mapped.issue_topic;
            }

            return mapped;
          };

          // Clean up _key property before sending, then map field names
          const cleanArray = (arr: any[]) =>
            arr.map(({ _key, ...rest }) => mapBackendFields(rest));

          // Calculate auto-accepted items (items not in reject or modify arrays)
          const rejectedIds = consolidatorFeedback.reject_array.map(
            (item: any) => item._key,
          );
          const modifiedIds = consolidatorFeedback.modify_array.map(
            (item: any) => item._key,
          );
          const explicitlyAcceptedIds = consolidatorFeedback.accept_array.map(
            (item: any) => item._key,
          );

          const autoAcceptedItems = consolidatorFeedback.consolidated_issues
            .filter(
              (issue: any) =>
                !rejectedIds.includes(issue.issue_id) &&
                !modifiedIds.includes(issue.issue_id) &&
                !explicitlyAcceptedIds.includes(issue.issue_id),
            )
            .map((issue: any) => ({ ...issue, _key: issue.issue_id }));

          // Combine explicitly accepted items with auto-accepted items
          const finalAcceptArray = [
            ...consolidatorFeedback.accept_array,
            ...autoAcceptedItems,
          ];

          payload.consolidator_accept_array = cleanArray(finalAcceptArray);
          payload.consolidator_reject_array = cleanArray(
            consolidatorFeedback.reject_array,
          );
          payload.consolidator_modify_array = cleanArray(
            consolidatorFeedback.modify_array,
          );

          // Reset the arrays after including in payload
          dispatch(resetConsolidatorFeedback());
        }

        // Include individual review feedbacks (Finance, Legal, Procurement, Business)
        // Structure: accept_array = accepted items, reject_array = rejected items, modify_array = modifications, new_array = newly added items

        // Helper to process feedback for each review type
        const processFeedback = (feedback: any) => {
          // Collect all new items (with _isNew flag) from all sections
          const newItems = [
            ...feedback.modify_array.filter((item: any) => item._isNew),
            ...feedback.key_findings_modifications.filter(
              (item: any) => item._isNew,
            ),
            ...feedback.deviations_modifications.filter(
              (item: any) => item._isNew,
            ),
            ...feedback.gaps_modifications.filter((item: any) => item._isNew),
          ];

          // Collect all modified items (without _isNew flag) from all sections
          const modifiedItems = [
            ...feedback.modify_array.filter((item: any) => !item._isNew),
            ...feedback.key_findings_modifications.filter(
              (item: any) => !item._isNew,
            ),
            ...feedback.deviations_modifications.filter(
              (item: any) => !item._isNew,
            ),
            ...feedback.gaps_modifications.filter((item: any) => !item._isNew),
          ];

          // Map frontend field names back to backend field names
          const mapBackendFields = (item: any) => {
            const mapped = { ...item };

            // KeyFinding: issue_clause → issue_or_clause (backend accepts both)
            if (mapped.issue_clause !== undefined) {
              mapped.issue_or_clause = mapped.issue_clause;
              delete mapped.issue_clause;
            }

            // RFPDeviation: section → issue (backend expects 'issue' as identifier)
            if (
              mapped.section !== undefined &&
              mapped.observed_deviation !== undefined
            ) {
              mapped.issue = mapped.section;
              delete mapped.section;
            }

            // KeyGapRisk: title → issue_title (only for gaps which have section + title)
            if (mapped.title !== undefined && mapped.section !== undefined) {
              mapped.issue_title = mapped.title;
              delete mapped.title;
            }

            // NegotiationPosition: topic_issue → issue (backend expects 'issue')
            if (
              mapped.topic_issue !== undefined &&
              mapped.primary_owner !== undefined
            ) {
              mapped.issue = mapped.topic_issue;
              delete mapped.topic_issue;
            }

            // PlaybookEntry (Consolidator): issue_topic → issue (backend expects 'issue')
            if (
              mapped.issue_topic !== undefined &&
              mapped.primary_position !== undefined
            ) {
              mapped.issue = mapped.issue_topic;
              delete mapped.issue_topic;
            }

            return mapped;
          };

          // Clean up _key and _isNew properties, then map field names
          const cleanArray = (arr: any[]) =>
            arr.map(({ _key, _isNew, ...rest }) => mapBackendFields(rest));

          return {
            accept_array: cleanArray(feedback.accept_array),
            reject_array: cleanArray(feedback.reject_array),
            modify_array: cleanArray(modifiedItems),
            new_array: cleanArray(newItems),
          };
        };

        const processedFinance = processFeedback(financeFeedback);
        const processedLegal = processFeedback(legalFeedback);
        const processedProcurement = processFeedback(procurementFeedback);
        const processedBusiness = processFeedback(businessFeedback);

        const hasFinanceFeedback =
          processedFinance.accept_array.length > 0 ||
          processedFinance.reject_array.length > 0 ||
          processedFinance.modify_array.length > 0 ||
          processedFinance.new_array.length > 0;

        const hasLegalFeedback =
          processedLegal.accept_array.length > 0 ||
          processedLegal.reject_array.length > 0 ||
          processedLegal.modify_array.length > 0 ||
          processedLegal.new_array.length > 0;

        const hasProcurementFeedback =
          processedProcurement.accept_array.length > 0 ||
          processedProcurement.reject_array.length > 0 ||
          processedProcurement.modify_array.length > 0 ||
          processedProcurement.new_array.length > 0;

        const hasBusinessFeedback =
          processedBusiness.accept_array.length > 0 ||
          processedBusiness.reject_array.length > 0 ||
          processedBusiness.modify_array.length > 0 ||
          processedBusiness.new_array.length > 0;

        // Only send arrays if:
        // 1. There are changes to send, AND
        // 2. This is NOT a simple approval (i.e., user is actively submitting changes)
        const shouldSendArrays =
          (hasFinanceFeedback ||
            hasLegalFeedback ||
            hasProcurementFeedback ||
            hasBusinessFeedback) &&
          !isSimpleApproval;

        if (shouldSendArrays) {
          // Combine all arrays from all review types (same pattern as consolidator)
          const allAcceptItems = [
            ...processedFinance.accept_array,
            ...processedLegal.accept_array,
            ...processedProcurement.accept_array,
            ...processedBusiness.accept_array,
          ];

          const allRejectItems = [
            ...processedFinance.reject_array,
            ...processedLegal.reject_array,
            ...processedProcurement.reject_array,
            ...processedBusiness.reject_array,
          ];

          const allModifyItems = [
            ...processedFinance.modify_array,
            ...processedLegal.modify_array,
            ...processedProcurement.modify_array,
            ...processedBusiness.modify_array,
          ];

          const allNewItems = [
            ...processedFinance.new_array,
            ...processedLegal.new_array,
            ...processedProcurement.new_array,
            ...processedBusiness.new_array,
          ];

          // Add to payload as JSON strings (same format as consolidator)
          if (allAcceptItems.length > 0) {
            payload.contract_reviewer_accept_array =
              JSON.stringify(allAcceptItems);
          }
          if (allRejectItems.length > 0) {
            payload.contract_reviewer_reject_array =
              JSON.stringify(allRejectItems);
          }
          if (allModifyItems.length > 0) {
            payload.contract_reviewer_modify_array =
              JSON.stringify(allModifyItems);
          }
          if (allNewItems.length > 0) {
            payload.contract_reviewer_new_array = JSON.stringify(allNewItems);
          }

          // NOTE: Don't reset modifications here - keep them so previous message bubbles
          // can continue displaying user additions even after approval.
          // Modifications will be reset when starting a new review session (new agent/new chat).
          // if (hasFinanceFeedback) dispatch(resetFinanceFeedback());
          // if (hasLegalFeedback) dispatch(resetLegalFeedback());
          // if (hasProcurementFeedback) dispatch(resetProcurementFeedback());
          // if (hasBusinessFeedback) dispatch(resetBusinessFeedback());
        }

        // Include SOW Auto-Filler modification array if any exist
        // Only get feedback from the most recent SOW message to avoid sending old modifications
        const mostRecentSowMessage = currentChat
          .slice()
          .reverse()
          .find(
            (msg) =>
              msg.sender === "agent" && msg.agentIdName === "sow_auto_filler",
          );

        // Clean up old SOW feedback entries for messages that no longer exist in chat
        // This ensures we don't accumulate stale feedback in Redux
        const sowMessageIds = currentChat
          .filter(
            (msg) =>
              msg.sender === "agent" && msg.agentIdName === "sow_auto_filler",
          )
          .map((msg) => msg.id);

        Object.keys(sowAutoFillerFeedback).forEach((id) => {
          if (!sowMessageIds.includes(id)) {
            dispatch(resetSowAutoFillerFeedback(id));
          }
        });

        // Only include feedback from the most recent message when sending to backend
        if (mostRecentSowMessage?.id) {
          const sowFeedback = sowAutoFillerFeedback[mostRecentSowMessage.id];
          if (sowFeedback && sowFeedback.sow_modify_array.length > 0) {
            payload.modify_array = sowFeedback.sow_modify_array;
          }
        }

        // Include Human Capital feedback arrays if any exist (for HumanCapitalAgent - Agent ID 55)
        // Only modify_array is supported (accept/reject removed)
        if (humanCapitalFeedback.modify_array.length > 0) {
          console.log("Human Capital - Adding feedback to payload:", humanCapitalFeedback);

          payload.modify_array = humanCapitalFeedback.modify_array;

          console.log("Human Capital - Payload updated:", {
            modify_array: payload.modify_array,
          });

          // Reset after sending
          dispatch(resetHumanCapitalFeedback());
        }

        dispatch(sendMessageToAgent(payload));
      } catch (error) {
        console.error("Error while sending agent query", error);
      } finally {
        setValue("");
        setSelectedFiles([]);
        // Clear dual file uploads for agent 52
        if (currentAgent?.id === 52) {
          setOurDocument(null);
          setCounterpartyDocument(null);
          setThreadId("");
          setResumeNegotiation(false);
        }
        // Clear JD file upload for agent 55
        if (currentAgent?.id === 55) {
          setJdDocument(null);
        }
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      // Submit on Enter (without Shift)
      if (e.key === "Enter" && !e.shiftKey && !loading) {
        e.preventDefault(); // Prevent new line

        // Check if submission should be prevented based on agent-specific rules
        const shouldPreventSubmit =
          disabled ||
          hasEndFlowAgentResponded ||
          (currentAgent?.id === 52
            ? !((ourDocument && counterpartyDocument) || value.trim())
            : currentAgent?.id === 55
              ? !(jdDocument || value.trim())
              : (!value.trim() && selectedFiles.length === 0) ||
                (currentAgent?.id === 20 && !isProcurementStageSelected) ||
                (currentAgent?.id === 18 && !value.trim()) ||
                (currentAgent?.id === 30 &&
                  (isAgent30FirstMessage
                    ? !value.trim() || selectedFiles.length === 0
                    : !value.trim())));

        if (!shouldPreventSubmit) {
          handleSubmit();
        }
      }
      // Allow Shift+Enter for new line (default behavior)
    };

    // Keep input value in sync with speech transcript when listening
    useEffect(() => {
      if (listening) {
        setValue(baseValueRef.current + (transcript || ""));
      } else {
        if (transcript) {
          setValue(baseValueRef.current + transcript);
          baseValueRef.current = "";
          resetTranscript();
        }
      }
    }, [transcript, listening]);

    // Stop listening if input becomes disabled
    useEffect(() => {
      if (disabled && listening) {
        SpeechRecognition.stopListening();
      }
    }, [disabled, listening]);

    const formatFileSize = (bytes: number): string => {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    };

    // Determine if HR filters should be shown
    const shouldShowFilters = useMemo(() => {
      const hasAgentResponse = currentChat.some(
        (msg) => msg.sender === "agent",
      );

      // If no agent response yet, show filters
      if (!hasAgentResponse) return true;

      // Find the last agent message
      const lastAgentMessage = [...currentChat]
        .reverse()
        .find((msg) => msg.sender === "agent");

      // Keep filters visible if last response is from specific agents
      return AGENTS_WITH_PERSISTENT_HR_FILTERS.includes(
        lastAgentMessage?.agentIdName || "",
      );
    }, [currentChat]);

    // For agent 103, check if documents have already been uploaded
    // Once uploaded, disable further uploads
    const hasAgent103UploadedDocuments = useMemo(() => {
      if (currentAgent?.id !== 103) return false;
      return currentChat.some(
        (msg) => msg.sender === "user" && msg.files && msg.files.length > 0,
      );
    }, [currentAgent?.id, currentChat]);

    // Determine if file attachment should be shown
    // For agent 103, hide attachment after first upload
    // For agent 52 and 55, hide standard attachment (they have their own inline upload UI)
    const shouldShowFileAttachment = useMemo(() => {
      const hasFileAttachment = AGENTS_WITH_FILE_ATTACHMENT.includes(
        currentAgent?.id || 0,
      );
      if (!hasFileAttachment) return false;

      // For agent 52 and 55, hide standard file attachment (uses inline upload)
      if (currentAgent?.id === 52 || currentAgent?.id === 55) return false;

      // For agent 103, hide file attachment if documents have already been uploaded
      if (currentAgent?.id === 103 && hasAgent103UploadedDocuments) {
        return false;
      }

      // For agent 48 (NDA), only offer upload on the home/zeroth step. Gate on user
      // messages (not agent) — the welcome greeting is an agent message, so checking
      // for an agent message would hide the button before the user starts.
      if (
        currentAgent?.id === 48 &&
        currentChat.some((msg) => msg.sender === "user")
      ) {
        return false;
      }

      return true;
    }, [currentAgent?.id, hasAgent103UploadedDocuments, currentChat]);

    // For agent 30, check if this is the first message (no agent response yet)
    const isAgent30FirstMessage = useMemo(() => {
      if (currentAgent?.id !== 30) return false;
      return !currentChat.some((msg) => msg.sender === "agent");
    }, [currentAgent?.id, currentChat]);

    // For agent 55, check if skill_mapping_agent has already responded
    // Once responded, disable JD upload
    const hasAgent55Responded = useMemo(() => {
      if (currentAgent?.id !== 55) return false;
      return currentChat.some(
        (msg) => msg.sender === "agent" && msg.agentIdName === "skill_mapping_agent",
      );
    }, [currentAgent?.id, currentChat]);

    // For agent 52, check if files have been uploaded at least once
    const hasAgent52UploadedFiles = useMemo(() => {
      if (currentAgent?.id !== 52) return false;
      // Check if there's any user message (indicates files were already uploaded)
      return currentChat.some((msg) => msg.sender === "user");
    }, [currentAgent?.id, currentChat]);

    // For agents with end-flow behavior, check if the agent has already provided a response
    // Once response is received, disable file uploads and input as the workflow is complete
    const hasEndFlowAgentResponded = useMemo(() => {
      if (!currentAgent?.id) return false;

      // Check if current agent is an end-flow agent
      const agentIdName = AGENTS_WITH_END_FLOW[currentAgent.id];
      if (!agentIdName) return false;

      // Check if this end-flow agent has already responded in the chat
      // For agent 52 (redline), check if any redline variant has responded
      if (currentAgent.id === 52) {
        return currentChat.some(
          (msg) => msg.sender === "agent" && msg.agentIdName === "redline", // Only exact match for final redline result
        );
      }

      // For other end-flow agents, use exact match
      return currentChat.some(
        (msg) => msg.sender === "agent" && msg.agentIdName === agentIdName,
      );
    }, [currentAgent?.id, currentChat]);

    // For agent 20, use procurement type to get dynamic placeholder
    // Hide placeholder after user sends first message
    const hasUserMessage = currentChat.some((msg) => msg.sender === "user");
    const placeholder = hasUserMessage
      ? ""
      : currentAgent?.id === 20
        ? PROCUREMENT_TYPE_OPTIONS.find(
            (opt) => opt.value === procurementFilters.procurementType,
          )?.placeholder || getPlaceholderByAgentId(agentId)
        : getPlaceholderByAgentId(agentId);

    // Check if procurement stage is selected for agent 20 (agent 21 always true)
    const isProcurementStageSelected =
      currentAgent?.id === 20
        ? procurementFilters.procurementType !== ""
        : true; // Agent 21 and others always true

    // Clear selected files for agent 103 if documents have already been uploaded
    useEffect(() => {
      if (
        currentAgent?.id === 103 &&
        hasAgent103UploadedDocuments &&
        selectedFiles.length > 0
      ) {
        setSelectedFiles([]);
      }
    }, [currentAgent?.id, hasAgent103UploadedDocuments, selectedFiles.length]);

    // Clear dual file uploads for end-flow agents when the agent has responded
    useEffect(() => {
      if (
        currentAgent?.id &&
        currentAgent.id in AGENTS_WITH_END_FLOW &&
        hasEndFlowAgentResponded
      ) {
        if (ourDocument) setOurDocument(null);
        if (counterpartyDocument) setCounterpartyDocument(null);
      }
    }, [
      currentAgent?.id,
      hasEndFlowAgentResponded,
      ourDocument,
      counterpartyDocument,
    ]);

    return (
      <div className={styles.messageInputContainer}>
        {/* Hidden file input for standard uploads */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept=".pdf,.doc,.docx,.txt,.xls,.xlsx"
          multiple
          style={{ display: "none" }}
        />

        {/* Hidden file inputs for agent 52 dual upload */}
        {currentAgent?.id === 52 && (
          <>
            <input
              type="file"
              ref={ourDocInputRef}
              onChange={(e) => handleDualFileSelect(e, "our")}
              accept=".pdf,.doc,.docx"
              disabled={hasEndFlowAgentResponded}
              style={{ display: "none" }}
            />
            <input
              type="file"
              ref={counterpartyDocInputRef}
              onChange={(e) => handleDualFileSelect(e, "counterparty")}
              accept=".pdf,.doc,.docx"
              disabled={hasEndFlowAgentResponded}
              style={{ display: "none" }}
            />
          </>
        )}

        {/* Hidden file input for agent 55 JD upload */}
        {currentAgent?.id === 55 && (
          <input
            type="file"
            ref={jdDocInputRef}
            onChange={handleJdFileSelect}
            accept=".pdf,.doc,.docx"
            disabled={hasAgent55Responded}
            style={{ display: "none" }}
          />
        )}

        {/* Display selected files for agent 52 */}
        {currentAgent?.id === 52 && (ourDocument || counterpartyDocument) && (
          <div className={styles.selectedFilesContainer}>
            {ourDocument && (
              <Chip
                label={`Our document: ${ourDocument.name}`}
                onDelete={
                  hasEndFlowAgentResponded
                    ? undefined
                    : () => handleRemoveDualFile("our")
                }
                deleteIcon={<CloseIcon />}
                size="small"
                className={styles.fileChip}
              />
            )}
            {counterpartyDocument && (
              <Chip
                label={`Counterparty document: ${counterpartyDocument.name}`}
                onDelete={
                  hasEndFlowAgentResponded
                    ? undefined
                    : () => handleRemoveDualFile("counterparty")
                }
                deleteIcon={<CloseIcon />}
                size="small"
                className={styles.fileChip}
              />
            )}
          </div>
        )}

        {/* Display selected file for agent 55 */}
        {currentAgent?.id === 55 && jdDocument && (
          <div className={styles.selectedFilesContainer}>
            <Chip
              label={`JD: ${jdDocument.name}`}
              onDelete={handleRemoveJdFile}
              deleteIcon={<CloseIcon />}
              size="small"
              className={styles.fileChip}
            />
          </div>
        )}

        {/* Display selected files for standard agents */}
        {selectedFiles.length > 0 && (
          <div className={styles.selectedFilesContainer}>
            {(currentAgent?.id === 18
              ? selectedFiles.slice(0, 6)
              : selectedFiles
            ).map((file, index) => (
              <Chip
                key={index}
                label={
                  currentAgent?.id === 18
                    ? file.name
                    : `${file.name} (${formatFileSize(file.size)})`
                }
                onDelete={() => handleRemoveFile(index)}
                deleteIcon={<CloseIcon />}
                size="small"
                className={styles.fileChip}
              />
            ))}
            {currentAgent?.id === 18 && selectedFiles.length > 6 && (
              <Chip
                label={`+${selectedFiles.length - 6} more`}
                size="small"
                className={styles.fileChip}
                onClick={() => setShowAllFilesDialog(true)}
                clickable
              />
            )}
          </div>
        )}

        {/* ...existing code... (buttonRow removed as requested) */}
        <div className={styles.inputBar}>
          <div className={styles.textareaContainer}>
            <TextField
              inputRef={inputRef}
              fullWidth
              // placeholder="Start with your prompt— the agent will gather any details needed for quality output, as needed ..."
              placeholder={placeholder}
              value={value}
              multiline
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              variant="outlined"
              className={styles.chatInput}
              disabled={
                disabled ||
                (!!currentAgent?.id &&
                  currentAgent.id in AGENTS_WITH_END_FLOW &&
                  hasEndFlowAgentResponded)
              }
            />

            {shouldShowFileAttachment && (
              <img
                src={attachIcon}
                alt="attach"
                className={styles.leftIcon}
                title={
                  currentAgent?.id === 18
                    ? "Upload resume"
                    : currentAgent?.id === 46
                      ? "Upload contract template"
                      : currentAgent?.id === 106
                        ? "Upload document"
                        : currentAgent?.id === 48
                          ? "Upload a document to start your NDA"
                          : currentAgent?.id === 55
                            ? "Upload JD"
                            : currentAgent?.id === 54
                              ? "Upload MOU document (PDF or DOCX)"
                              : ""
                }
                onClick={() => {
                  // For agent 18, check if upload profiles toggle is on
                  if (currentAgent?.id === 18 && !isUploadProfilesOn) return;
                  // For agent 103, prevent upload if documents already uploaded
                  if (currentAgent?.id === 103 && hasAgent103UploadedDocuments)
                    return;
                  // For other agents, check their specific conditions
                  if (
                    !disabled &&
                    (currentAgent?.id === 18 ||
                      currentAgent?.id === 21 ||
                      currentAgent?.id === 46 ||
                      currentAgent?.id === 55 ||
                      currentAgent?.id === 106 ||
                      currentAgent?.id === 103 ||
                      currentAgent?.id === 48 ||
                      currentAgent?.id === 54 ||
                      isProcurementStageSelected)
                  ) {
                    fileInputRef.current?.click();
                  }
                }}
                style={{
                  cursor:
                    disabled ||
                    (currentAgent?.id === 20 && !isProcurementStageSelected) ||
                    (currentAgent?.id === 18 && !isUploadProfilesOn) ||
                    (currentAgent?.id === 103 && hasAgent103UploadedDocuments)
                      ? "not-allowed"
                      : "pointer",
                  opacity:
                    disabled ||
                    (currentAgent?.id === 20 && !isProcurementStageSelected) ||
                    (currentAgent?.id === 18 && !isUploadProfilesOn) ||
                    (currentAgent?.id === 103 && hasAgent103UploadedDocuments)
                      ? 0.5
                      : 1,
                }}
              />
            )}

            <div className={styles.rightIcons}>
              {/* Translation controls for agent ID 7 */}
              {currentAgent?.id === 7 && (
                <>
                  {/* Translation Direction Button Group */}
                  <div className={styles.translationDirectionGroup}>
                    <span className={styles.translationLabel}>
                      Translation Direction:
                    </span>
                    <ToggleButtonGroup
                      value={translationFilters.translation}
                      exclusive
                      onChange={(_, newValue) => {
                        if (newValue !== null) {
                          dispatch(
                            setTranslationFilters({ translation: newValue }),
                          );
                        }
                      }}
                      size="small"
                      disabled={loading}
                      aria-label="Translation direction selector"
                      sx={{
                        border: "1px solid #00403e1a",
                        borderRadius: "50px",
                        backgroundColor: "#00403e03",
                        padding: "4px",
                        "& .MuiToggleButtonGroup-grouped": {
                          border: "none",
                          borderRadius: "50px !important",
                          margin: 0,
                          "&:not(:first-of-type)": {
                            marginLeft: "4px",
                          },
                        },
                        "& .MuiToggleButton-root": {
                          px: 2.5,
                          py: 0.75,
                          fontSize: "14px",
                          fontWeight: 500,
                          textTransform: "none",
                          color: "#00403e",
                          border: "none",
                          transition: "all 0.25s ease",
                          "&.Mui-selected": {
                            backgroundColor: "#004140",
                            color: "#ffffff",
                            "&:hover": {
                              backgroundColor: "#003030",
                            },
                          },
                          "&:hover": {
                            backgroundColor: "rgba(0, 64, 62, 0.08)",
                          },
                          "&.Mui-disabled": {
                            opacity: 0.5,
                            cursor: "not-allowed",
                          },
                        },
                      }}
                    >
                      <ToggleButton
                        value="arabic"
                        aria-label="English to Arabic translation"
                      >
                        English to Arabic
                      </ToggleButton>
                      <ToggleButton
                        value="english"
                        aria-label="Arabic to English translation"
                      >
                        Arabic to English
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </div>

                  {/* Legal Translation Toggle */}
                  <div
                    className={styles.tooltipWrapper}
                    style={{ position: "relative" }}
                  >
                    <div
                      className={`${styles.toggleContainer} ${loading ? styles.disabled : ""}`}
                      onClick={() => {
                        if (!loading) {
                          const newType =
                            translationFilters.translationType === "legal"
                              ? "general"
                              : "legal";
                          dispatch(
                            setTranslationFilters({ translationType: newType }),
                          );
                        }
                      }}
                      aria-label={
                        loading
                          ? "Legal translation toggle disabled"
                          : translationFilters.translationType === "legal"
                            ? "Disable legal translation"
                            : "Enable legal translation"
                      }
                      style={{
                        cursor: loading ? "not-allowed" : "pointer",
                        position: "relative",
                      }}
                    >
                      <button
                        type="button"
                        className={`${styles.toggleBtn} ${translationFilters.translationType === "legal" ? styles.on : styles.off} ${loading ? styles.disabled : ""}`}
                        disabled={loading}
                      >
                        <span className={styles.knob} />
                      </button>
                      <span className={styles.tooltip}>
                        {loading
                          ? ""
                          : translationFilters.translationType === "legal"
                            ? "Disable legal translation"
                            : "Enable legal translation"}
                      </span>
                      <span className={styles.toggleText}>
                        Enable legal translation
                      </span>
                    </div>
                  </div>
                </>
              )}

              {showUploadProfilesToggle && (
                <div className={styles.tooltipWrapper}>
                  <div
                    className={`${styles.toggleContainer} ${disableUploadProfilesToggle ? styles.disabled : ""}`}
                    onClick={() =>
                      !disableUploadProfilesToggle &&
                      setIsUploadProfilesOn(!isUploadProfilesOn)
                    }
                    aria-label={
                      disableUploadProfilesToggle
                        ? "Upload profiles toggle disabled"
                        : isUploadProfilesOn
                          ? "Disable upload profiles"
                          : "Enable upload profiles"
                    }
                    style={{
                      cursor: disableUploadProfilesToggle
                        ? "not-allowed"
                        : "pointer",
                    }}
                  >
                    <button
                      type="button"
                      className={`${styles.toggleBtn} ${isUploadProfilesOn ? styles.on : styles.off} ${disableUploadProfilesToggle ? styles.disabled : ""}`}
                      disabled={disableUploadProfilesToggle}
                    >
                      <span className={styles.knob} />
                    </button>
                    <span className={styles.toggleText}>Upload Resumes</span>
                  </div>
                </div>
              )}

              {showGenerateImageToggle && (
                <div className={styles.tooltipWrapper}>
                  <div
                    className={`${styles.toggleContainer} ${disableImageToggle ? styles.disabled : ""}`}
                    onClick={() => !disableImageToggle && setIsOn(!isOn)}
                    aria-label={
                      disableImageToggle
                        ? "Image generation toggle disabled"
                        : isOn
                          ? "Disable generate social media images"
                          : "Enable generate social media images"
                    }
                    style={{
                      cursor: disableImageToggle ? "not-allowed" : "pointer",
                    }}
                  >
                    <button
                      type="button"
                      className={`${styles.toggleBtn} ${isOn ? styles.on : styles.off} ${disableImageToggle ? styles.disabled : ""}`}
                      disabled={disableImageToggle}
                    >
                      <span className={styles.knob} />
                    </button>
                    <span className={styles.tooltip}>
                      {disableImageToggle
                        ? ""
                        : isOn
                          ? "Disable generate social media images"
                          : "Enable generate social media images"}
                    </span>
                    <span className={styles.toggleText}>
                      Generate social media images
                    </span>
                  </div>
                </div>
              )}
              {/* Show RefinementFilters based on shouldShowFilters logic */}
              {shouldShowFilters && (
                <RefinementFilters agentId={agentId} disabled={loading} />
              )}

              {/* Agent 55: Inline file upload control */}
              {currentAgent?.id === 55 && (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => jdDocInputRef.current?.click()}
                    disabled={disabled || loading || hasAgent55Responded}
                    startIcon={
                      jdDocument ? (
                        <span style={{ fontSize: "14px" }}>✓</span>
                      ) : (
                        <img
                          src={attachIcon}
                          alt="upload"
                          style={{ width: "12px", height: "12px" }}
                        />
                      )
                    }
                    sx={{
                      height: 26,
                      fontSize: "11px",
                      textTransform: "none",
                      minWidth: 90,
                      borderColor: jdDocument ? "#00d2aa" : "#ccc",
                      color: jdDocument ? "#004141" : "rgba(0,0,0,0.75)",
                      backgroundColor: jdDocument ? "#e8f5f3" : "transparent",
                      fontWeight: 500,
                      "&:hover": {
                        borderColor: "#00d2aa",
                        backgroundColor: "#f0faf8",
                      },
                      "&.Mui-disabled": {
                        opacity: 0.5,
                        borderColor: "#ccc",
                        backgroundColor: "#f5f5f5",
                        color: "#999",
                      },
                    }}
                  >
                    Upload JD
                  </Button>
                </Box>
              )}

              {/* Agent 52: Inline file upload controls */}
              {currentAgent?.id === 52 && (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <Box
                    component="span"
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "rgba(0,0,0,0.75)",
                      whiteSpace: "nowrap",
                      mr: 0.5,
                    }}
                  >
                    Upload Documents
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => ourDocInputRef.current?.click()}
                    disabled={
                      disabled ||
                      loading ||
                      hasAgent52UploadedFiles ||
                      hasEndFlowAgentResponded
                    }
                    startIcon={
                      ourDocument ? (
                        <span style={{ fontSize: "14px" }}>✓</span>
                      ) : (
                        <img
                          src={attachIcon}
                          alt="upload"
                          style={{ width: "12px", height: "12px" }}
                        />
                      )
                    }
                    sx={{
                      height: 26,
                      fontSize: "11px",
                      textTransform: "none",
                      minWidth: 100,
                      borderColor: ourDocument ? "#00d2aa" : "#ccc",
                      color: ourDocument ? "#004141" : "rgba(0,0,0,0.75)",
                      backgroundColor: ourDocument
                        ? "#e8f5f3"
                        : "transparent",
                      fontWeight: 500,
                      "&:hover": {
                        borderColor: "#00d2aa",
                        backgroundColor: "#f0faf8",
                      },
                    }}
                  >
                    Our document
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => counterpartyDocInputRef.current?.click()}
                    disabled={
                      disabled ||
                      loading ||
                      hasAgent52UploadedFiles ||
                      hasEndFlowAgentResponded
                    }
                    startIcon={
                      counterpartyDocument ? (
                        <span style={{ fontSize: "14px" }}>✓</span>
                      ) : (
                        <img
                          src={attachIcon}
                          alt="upload"
                          style={{ width: "12px", height: "12px" }}
                        />
                      )
                    }
                    sx={{
                      height: 26,
                      fontSize: "11px",
                      textTransform: "none",
                      minWidth: 90,
                      borderColor: counterpartyDocument ? "#00d2aa" : "#ccc",
                      color: counterpartyDocument ? "#004141" : "rgba(0,0,0,0.75)",
                      backgroundColor: counterpartyDocument ? "#e8f5f3" : "transparent",
                      fontWeight: 500,
                      "&:hover": {
                        borderColor: "#00d2aa",
                        backgroundColor: "#f0faf8",
                      },
                    }}
                  >
                    Counterparty document
                  </Button>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={resumeNegotiation}
                        onChange={(e) => {
                          setResumeNegotiation(e.target.checked);
                          if (!e.target.checked) {
                            setThreadId("");
                          }
                        }}
                        disabled={disabled || loading || hasAgent52UploadedFiles || hasEndFlowAgentResponded}
                        sx={{
                          padding: "4px",
                          color: "#00a888",
                          "&.Mui-checked": {
                            color: "#00a888",
                          },
                        }}
                      />
                    }
                    label="Resume Previous Negotiation"
                    sx={{
                      ml: 1.5,
                      mr: 0,
                      "& .MuiFormControlLabel-label": {
                        fontSize: 12,
                        fontWeight: 600,
                        color: "rgba(0,0,0,0.75)",
                      },
                    }}
                  />
                  {resumeNegotiation && (
                    <>
                      <Box
                        component="span"
                        sx={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "rgba(0,0,0,0.75)",
                          whiteSpace: "nowrap",
                          ml: 1.5,
                          mr: 0.5,
                        }}
                      >
                        Thread ID
                      </Box>
                      <TextField
                        size="small"
                        placeholder="Enter thread ID"
                        value={threadId}
                        onChange={(e) => setThreadId(e.target.value)}
                        disabled={disabled || loading || hasAgent52UploadedFiles || hasEndFlowAgentResponded}
                        sx={{
                          width: 150,
                          "& .MuiOutlinedInput-root": {
                            height: 26,
                            fontSize: "11px",
                            backgroundColor: "#fff",
                            "& fieldset": {
                              borderColor: "#ccc",
                            },
                            "&:hover fieldset": {
                              borderColor: "#00a888",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#00a888",
                            },
                            "&.Mui-disabled": {
                              backgroundColor: "#f5f5f5",
                            },
                          },
                          "& .MuiOutlinedInput-input": {
                            padding: "5px 14px !important",
                            fontSize: "11px",
                            color: "#004141",
                            lineHeight: "16px",
                            "&::placeholder": {
                              color: "rgba(0,0,0,0.4)",
                              opacity: 1,
                              fontSize: "11px",
                              lineHeight: "16px",
                              paddingLeft: "0",
                            },
                          },
                        }}
                      />
                    </>
                  )}
                </Box>
              )}

              <button
                aria-pressed={listening}
                title={listening ? "Stop recording" : "Start voice input"}
                disabled={
                  disabled ||
                  (!!currentAgent?.id &&
                    currentAgent.id in AGENTS_WITH_END_FLOW &&
                    hasEndFlowAgentResponded)
                }
                onClick={() => {
                  if (!browserSupportsSpeechRecognition) {
                    alert(
                      "Speech recognition is not supported in this browser.",
                    );
                    return;
                  }
                  if (listening) {
                    SpeechRecognition.stopListening();
                  } else {
                    baseValueRef.current = value || "";
                    resetTranscript();
                    SpeechRecognition.startListening({
                      continuous: true,
                      language: navigator.language || "en-US",
                    });
                  }
                }}
                className={styles.iconButton}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor:
                    disabled ||
                    (!!currentAgent?.id &&
                      currentAgent.id in AGENTS_WITH_END_FLOW &&
                      hasEndFlowAgentResponded)
                      ? "not-allowed"
                      : "pointer",
                  opacity:
                    disabled ||
                    (!!currentAgent?.id &&
                      currentAgent.id in AGENTS_WITH_END_FLOW &&
                      hasEndFlowAgentResponded)
                      ? 0.5
                      : 1,
                }}
              >
                <img
                  src={micIcon}
                  alt={listening ? "listening" : "mic"}
                  className={listening ? styles.iconActive : styles.icon}
                />
              </button>
              <button
                className="chat-input-icon"
                aria-label="Waveform"
                disabled={
                  disabled ||
                  loading ||
                  hasEndFlowAgentResponded ||
                  (currentAgent?.id === 52
                    ? !((ourDocument && counterpartyDocument) || value.trim())
                    : currentAgent?.id === 55
                      ? !(jdDocument || value.trim())
                      : (!value.trim() && selectedFiles.length === 0) ||
                        (currentAgent?.id === 20 &&
                          !isProcurementStageSelected) ||
                        (currentAgent?.id === 18 && !value.trim()) ||
                        (currentAgent?.id === 30 &&
                          (isAgent30FirstMessage
                            ? !value.trim() || selectedFiles.length === 0
                            : !value.trim())))
                }
                onClick={handleSubmit}
              >
                {loading ? (
                  <LoadingSpinner inline size="sm" />
                ) : (
                  <img src={sendIcon} alt="send" className="icon" />
                )}
              </button>
            </div>
          </div>
          {/* <IconButton
            aria-label="Enhance Prompt"
            title={enhancing ? "Enhancing..." : "Enhance Prompt"}
            className={styles.enhanceButton}
            disabled={!value.trim() || enhancing}
            onClick={async () => {
              if (!value.trim() || enhancing) return;
              const enhanced = await enhance(value.trim());
              if (enhanced && enhanced.trim()) {
                setValue(enhanced);
              }
            }}
          >
            {enhancing ? <CircularProgress size={20} /> : <AutoFixHighIcon />}
          </IconButton> */}
          {/* <IconButton
            onClick={handleSubmit}
            title="Send"
            className={styles.sendButton}
            disabled={!value.trim() || loading}
          >
            {loading && <LoadingSpinner inline size="xs" />}
          </IconButton> */}
        </div>
        <Dialog
          open={showAllFilesDialog}
          onClose={() => setShowAllFilesDialog(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Uploaded resumes</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ maxHeight: 360, overflowY: "auto" }}>
              {selectedFiles.map((file, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {file.name}
                  </Box>
                  <IconButton
                    aria-label="Remove file"
                    size="small"
                    onClick={() => handleRemoveFile(index)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAllFilesDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>
        {showDisclaimer && (
          <p className={styles.chatDisclaimer}>
            AI may display inaccurate info, so please double check the response.
          </p>
        )}
      </div>
    );
  },
);
export default MessageInput;
